import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPOIIconDataURL } from '../../../../ui/icons/poi';

export type POIType = 'restaurants' | 'groceries' | 'cafes' | 'shopping' | 'nightlife';

type POIPlace = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  type: POIType;
  rating?: number;
  priceLevel?: number;
};

type POIConfig = {
  placesTypes: Array<string>;
  color: string;
  displayName: string;
};

type POIMarkersProps = {
  map: google.maps.Map;
  centerLat: number;
  centerLng: number;
  activePOITypes: Set<POIType>;
  onPOILoaded?: (results: Array<POIPlace>, poiType: POIType) => void;
  onPOIError?: (error: Error, poiType: POIType) => void;
};

// POI search configuration - maps our pill types to Google Places API types
// Updated to use Panda CSS design tokens and enhanced with icon support
const POI_SEARCH_CONFIGS: Record<POIType, POIConfig> = {
  restaurants: {
    placesTypes: ['restaurant', 'meal_takeaway'],
    color: 'hpxTeal500', // Design token for teal background
    displayName: 'Restaurant',
  },
  groceries: {
    placesTypes: ['grocery_or_supermarket', 'supermarket'],
    color: 'hpxRed400', // Design token for red background
    displayName: 'Grocery Store',
  },
  cafes: {
    placesTypes: ['cafe'],
    color: 'hpPurple500', // Design token for purple background
    displayName: 'Cafe',
  },
  shopping: {
    placesTypes: ['shopping_mall', 'store'],
    color: 'hpxYellow600', // Design token for yellow background
    displayName: 'Shopping',
  },
  nightlife: {
    placesTypes: ['night_club', 'bar'],
    color: 'hpxBlue500', // Design token for blue background
    displayName: 'Nightlife',
  },
};

/**
 * POIMarkers - Displays POI markers on the map based on active POI types
 *
 * Features modern 24x24px circular markers with meaningful 16x16px icons:
 * - Restaurants: Fork/knife icon on teal background (hpxTeal500)
 * - Groceries: Shopping cart icon on red background (hpxRed400)
 * - Cafes: Coffee cup icon on purple background (hpPurple500)
 * - Shopping: Shopping bag icon on yellow background (hpxYellow600)
 * - Nightlife: Music/disco icon on blue background (hpxBlue500)
 *
 * Searches for places near the center coordinates using Google Places API.
 */
export const POIMarkers = ({ map, centerLat, centerLng, activePOITypes, onPOILoaded, onPOIError }: POIMarkersProps) => {
  // Store markers and places state
  const [poiMarkers, setPOIMarkers] = useState<Map<string, google.maps.Marker>>(new Map());
  const [poiPlaces, setPOIPlaces] = useState<Map<POIType, Array<POIPlace>>>(new Map());
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  // Enhanced state management with refs for current state access
  const isMountedRef = useRef(true);
  const markersRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const placesRef = useRef<Map<POIType, Array<POIPlace>>>(new Map());
  const activeRequestsRef = useRef<Set<POIType>>(new Set());

  // Initialize Places service when map is available
  useEffect(() => {
    if (map && !placesServiceRef.current) {
      placesServiceRef.current = new google.maps.places.PlacesService(map);
    }
  }, [map]);

  // Create marker icon for POI type using modern icon system
  // Generates 14x14px circular markers with design token colors and centered icons
  const createPOIMarkerIcon = (poiType: POIType): google.maps.Icon => {
    const config = POI_SEARCH_CONFIGS[poiType];
    const size = 24; // 24x24px circular markers with 16x16px icons

    return {
      url: createPOIIconDataURL(poiType, config.color, size),
      size: new google.maps.Size(size, size),
      scaledSize: new google.maps.Size(size, size),
      anchor: new google.maps.Point(size / 2, size / 2),
    };
  };

  // Search for POI places of a specific type - enhanced with async safety
  const searchPOIType = useCallback(
    (poiType: POIType) => {
      if (!isMountedRef.current || !placesServiceRef.current) return;

      // Track active request
      activeRequestsRef.current.add(poiType);

      const config = POI_SEARCH_CONFIGS[poiType];
      const center = new google.maps.LatLng(centerLat, centerLng);

      placesServiceRef.current.nearbySearch(
        {
          location: center,
          radius: 800, // 800m radius - same as AmenityMarkers
          type: config.placesTypes[0] as any, // Use first type for search
        },
        (results, status) => {
          // Cancel if component unmounted or request canceled
          if (!isMountedRef.current || !activeRequestsRef.current.has(poiType)) {
            return;
          }

          // Remove from active requests
          activeRequestsRef.current.delete(poiType);

          if (status === google.maps.places.PlacesServiceStatus.OK && results) {
            // Transform results to our POIPlace format
            const places: Array<POIPlace> = results.slice(0, 6).map((place, index) => ({
              id: place.place_id || `${poiType}-${index}`,
              name: place.name || 'Unknown',
              lat: place.geometry?.location?.lat() || centerLat,
              lng: place.geometry?.location?.lng() || centerLng,
              type: poiType,
              rating: place.rating,
              priceLevel: place.price_level,
            }));

            // Update places state with both ref and setState
            const newPlaces = new Map(placesRef.current).set(poiType, places);
            placesRef.current = newPlaces;
            setPOIPlaces(newPlaces);

            // Create markers for these places
            createMarkersForPlaces(places, poiType);

            // Notify parent of successful load
            if (onPOILoaded) {
              onPOILoaded(places, poiType);
            }
          } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            const newPlaces = new Map(placesRef.current).set(poiType, []);
            placesRef.current = newPlaces;
            setPOIPlaces(newPlaces);

            if (onPOILoaded) {
              onPOILoaded([], poiType);
            }
          } else {
            console.warn(`POI search failed for ${poiType}:`, status);
            if (onPOIError) {
              onPOIError(new Error(`Failed to search for ${poiType}: ${status}`), poiType);
            }
          }
        },
      );
    },
    [centerLat, centerLng, onPOIError, onPOILoaded],
  );

  // Create markers for places - enhanced with mount safety and ref management
  const createMarkersForPlaces = useCallback(
    (places: Array<POIPlace>, poiType: POIType) => {
      if (!isMountedRef.current || !map) return;

      const icon = createPOIMarkerIcon(poiType);
      const currentMarkers = markersRef.current;

      places.forEach((place) => {
        const markerId = `${poiType}-${place.id}`;

        // Double-check marker doesn't exist
        if (currentMarkers.has(markerId)) {
          return;
        }

        // Create marker only if still mounted
        if (!isMountedRef.current) return;

        const marker = new google.maps.Marker({
          position: { lat: place.lat, lng: place.lng },
          map: map,
          icon: icon,
          title: place.name,
          clickable: true,
        });

        // Add event listeners only if still mounted
        if (isMountedRef.current) {
          marker.addListener('click', () => {
            if (!isMountedRef.current) return;

            // Close existing info window
            if (infoWindowRef.current) {
              infoWindowRef.current.close();
            }

            // Create and show info window
            const infoWindow = new google.maps.InfoWindow({
              content: `
              <div style="padding: 8px; max-width: 200px;">
                <div style="font-weight: 600; color: #2f3c53; margin-bottom: 4px;">
                  ${place.name}
                </div>
                <div style="font-size: 12px; color: #6e6e6e; margin-bottom: 2px;">
                  ${POI_SEARCH_CONFIGS[place.type].displayName}
                </div>
                ${place.rating ? `<div style="font-size: 12px; color: #6e6e6e;">★ ${place.rating}</div>` : ''}
              </div>
            `,
            });

            infoWindow.open(map, marker);
            infoWindowRef.current = infoWindow;
          });

          // Update refs and state atomically
          const newMarkers = new Map(markersRef.current);
          newMarkers.set(markerId, marker);
          markersRef.current = newMarkers;
          setPOIMarkers(newMarkers);
        } else {
          // Cleanup if unmounted during creation
          marker.setMap(null);
        }
      });
    },
    [map],
  );

  // Remove markers for a specific POI type - enhanced with refs and mount checks
  const removeMarkersForType = useCallback((poiType: POIType) => {
    if (!isMountedRef.current) return;

    const currentMarkers = markersRef.current;
    const markersToRemove: Array<string> = [];

    // Use ref for current state, not closure state
    currentMarkers.forEach((marker, markerId) => {
      if (markerId.startsWith(`${poiType}-`)) {
        marker.setMap(null);
        markersToRemove.push(markerId);
      }
    });

    // Update both ref and state atomically
    const newMarkers = new Map(currentMarkers);
    markersToRemove.forEach((markerId) => {
      newMarkers.delete(markerId);
    });

    markersRef.current = newMarkers;
    setPOIMarkers(newMarkers);

    // Update places state
    const currentPlaces = placesRef.current;
    const newPlaces = new Map(currentPlaces);
    newPlaces.delete(poiType);

    placesRef.current = newPlaces;
    setPOIPlaces(newPlaces);
  }, []);

  // Handle changes to active POI types
  useEffect(() => {
    const currentPOITypes = new Set(placesRef.current.keys());

    // Add markers for newly active POI types
    activePOITypes.forEach((poiType) => {
      if (!currentPOITypes.has(poiType)) {
        searchPOIType(poiType);
      }
    });

    // Remove markers for deactivated POI types
    currentPOITypes.forEach((poiType) => {
      if (!activePOITypes.has(poiType)) {
        removeMarkersForType(poiType);
      }
    });
  }, [activePOITypes, removeMarkersForType, searchPOIType]);

  // Comprehensive cleanup on unmount - enhanced with refs and async safety
  useEffect(() => {
    return () => {
      // Mark as unmounted to cancel async operations
      isMountedRef.current = false;

      // Cancel all active requests
      activeRequestsRef.current.clear();

      // Close info window
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }

      // Remove ALL markers from map using refs for current state
      const currentMarkers = markersRef.current;
      currentMarkers.forEach((marker, markerId) => {
        marker.setMap(null);
      });

      // Clear all state
      markersRef.current = new Map();
      placesRef.current = new Map();
      setPOIMarkers(new Map());
      setPOIPlaces(new Map());
    };
  }, []);

  // This component doesn't render anything - it only manages markers
  return null;
};
